import React, {useEffect, useState} from "react";
import AppBar from "../components/app_bar/AppBar";
import Drawer from "../components/Drawer";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faClock, faStar, faPhone, faGlobe, faEye, faHeart, faThumbsUp } from '@fortawesome/free-solid-svg-icons';
import { 
    Typography, Container, CssBaseline, Box, Toolbar, Link, Chip, Grid, List, ListItem, ListItemText, ListItemIcon,
    Divider, Tooltip, Rating, Card, CardMedia, CardContent, Button, Badge,
} from '@mui/material';
import { useParams } from "react-router-dom";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DescriptionIcon from '@mui/icons-material/Description';
import PhotoIcon from '@mui/icons-material/Photo';
import MapIcon from '@mui/icons-material/Map';
import LabelIcon from '@mui/icons-material/Label';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { useLocation } from "react-router-dom";
import * as CONSTANTS from "../constants";
import "../styles/PlaceDetails.css";
import NotFound from "./NotFound";
import { getTagDisplayName } from "../mappings/placeCategories";

import { view_place } from "../services/topic_view";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import PlaceDetailsSkeleton from '../components/place/PlaceDetailsSkeleton';

// Fix leaflet's default icon path
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const PlaceDetails = () => {

    const location = useLocation();

    const [place, setPlace] = useState(null);
    const [loading, setLoading] = useState(true);
    let {lang, placeId} = useParams();

    const [drawerOpen, setDrawerOpen] = useState(false);
            
    const handleDrawerOpen = (open) => {
        if (open === undefined) {
            setDrawerOpen(!drawerOpen)
        } else {
            setDrawerOpen(open);
        }
    }

    // Helper function to get localized text
    const getLocalizedText = (textObj) => {
        if (!textObj) return '';
        return textObj[lang] || textObj.en || textObj.jp || textObj.tc || textObj.sc || '';
    };

    // Helper function to format date
    const formatDate = (dateObj) => {
        if (!dateObj || !dateObj.value) return 'Date not available';
        
        const date = new Date(dateObj.value);
        const parts = [];
        
        if (dateObj.has_year) parts.push(date.getFullYear());
        if (dateObj.has_month) parts.push(String(date.getMonth() + 1).padStart(2, '0'));
        if (dateObj.has_day) parts.push(String(date.getDate()).padStart(2, '0'));
        
        return parts.join('-') || 'Date not available';
    };

    // Helper function to format regular date
    const formatRegularDate = (dateString) => {
        if (!dateString) return 'Date not available';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    const fetchPlace = async () => {
        setLoading(true);
        const [status, result] = await view_place(lang, placeId.toString());
        if (status === 200 && result && result.place) {
            setPlace(result.place);
        } else {
            setPlace(null);
        }
        setLoading(false);
    }

    // send api request for url changes
    useEffect(() => {
        fetchPlace();
    }, [location.pathname, location.search]);
    

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <Container maxWidth="md">
                    <PlaceDetailsSkeleton />
                </Container>
            </Box>
        );
    }

    if (place) {

        // Get place name with fallback
        const placeName = getLocalizedText(place.name) || place.default_name || 'Unnamed Place';
        
        // Get location with fallback
        const placeLocation = getLocalizedText(place.location) || 'Location not specified';
        
        // Get description with fallback
        const placeDescription = getLocalizedText(place.description) || 'No description available';

        document.title = `${placeName} | ${CONSTANTS.DRAWER.PLACE[lang]} - Wotagei Zone`

        return (
            <Box >
                <CssBaseline />
                <AppBar lang={lang} handleDrawerOpen={handleDrawerOpen}/>
                <Box sx={{ display: 'flex' }}>
                    <Drawer lang={lang} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
                    <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                        <Toolbar />
                        <Container maxWidth="md">
                            <Typography>
                                <Link
                                    href={`/${lang}/place`}
                                    underline="hover"
                                    color={"textPrimary"}
                                >
                                    {CONSTANTS.DRAWER.PLACE[lang]}
                                </Link>
                                {` > ${placeName}`}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Typography 
                                variant="h4" 
                                gutterBottom
                                mt={4}
                                sx={{
                                    textAlign: { xs: 'center', md: 'left' },
                                    fontWeight: 700
                                }}
                                mb={4}
                            >
                                {placeName}
                            </Typography>

                            {/* Responsive Hero Section with Image and Details */}
                            <Grid container spacing={4} alignItems="flex-start">
                                {/* Main Image */}
                                {place.photos && place.photos.length > 0 && (
                                    <Grid item xs={12} md={5}>
                                        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                                            <CardMedia
                                                component="img"
                                                height="300"
                                                image={place.photos[0]}
                                                alt={placeName}
                                                sx={{ objectFit: 'cover', borderRadius: 3 }}
                                            />
                                        </Card>
                                    </Grid>
                                )}
                                {/* Details */}
                                <Grid item xs={12} md={7}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {/* UID */}
                                        {place.uid && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle2" fontWeight={600}>UID:</Typography>
                                                <Typography variant="body2">{place.uid}</Typography>
                                            </Box>
                                        )}
                                        {/* Location */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LocationOnIcon fontSize="small" />
                                            <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.PLACE_DETAILS.LOCATION[lang]}:</Typography>
                                            <Typography variant="body2">{placeLocation}</Typography>
                                        </Box>
                                        {/* Category */}
                                        {place.category && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <LabelIcon fontSize="small" />
                                                <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.PLACE_DETAILS.CATEGORY[lang]}:</Typography>
                                                <Typography variant="body2">{CONSTANTS.PLACE_CATEGORIES[place.category] ? CONSTANTS.PLACE_CATEGORIES[place.category][lang] : place.category}</Typography>
                                            </Box>
                                        )}
                                        {/* Tags */}
                                        {place.tags && place.tags.length > 0 && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <LabelIcon fontSize="small" />
                                                <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.PLACE_FILTER.TAGS[lang]}:</Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {place.tags.map((tagNumber) => (
                                                        <Chip
                                                            key={tagNumber.toString()} 
                                                            label={getTagDisplayName(tagNumber, lang)}
                                                            variant="outlined"
                                                            sx={{
                                                                borderRadius: '0.5rem',
                                                                borderColor: '#ccc',
                                                                fontSize: '0.85rem',
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Description Section */}
                            <Box mt={5} mb={3}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <DescriptionIcon sx={{ mr: 1 }} />
                                    <Typography variant="h6" fontWeight={700}>{CONSTANTS.PLACE_DETAILS.DESCRIPTION[lang]}</Typography>
                                </Box>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{placeDescription}</Typography>
                            </Box>

                            {/* Photos Section (additional) */}
                            {place.photos && place.photos.length > 1 && (
                                <Box mt={4} mb={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <PhotoIcon sx={{ mr: 1 }} />
                                        <Typography variant="h6" fontWeight={700}>{CONSTANTS.PLACE_DETAILS.PHOTOS[lang]}</Typography>
                                    </Box>
                                    <Grid container spacing={2}>
                                        {place.photos.slice(1).map((photo, index) => (
                                            <Grid item xs={12} sm={6} md={4} key={index}>
                                                <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                                                    <CardMedia
                                                        component="img"
                                                        height="180"
                                                        image={photo}
                                                        alt={`${placeName} photo ${index + 2}`}
                                                        sx={{ objectFit: 'cover', borderRadius: 2 }}
                                                    />
                                                </Card>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}

                            {/* Map Section */}
                            {place.coordinates && (
                                <Box mt={4} mb={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <MapIcon sx={{ mr: 1 }} />
                                        <Typography variant="h6" fontWeight={700}>{CONSTANTS.PLACE_DETAILS.MAP[lang]}</Typography>
                                    </Box>
                                    <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
                                        <CardContent>
                                            <Box
                                                sx={{
                                                    height: { xs: '250px', md: '400px' },
                                                    width: '100%',
                                                    borderRadius: 2,
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                <MapContainer
                                                    center={[place.coordinates.lat, place.coordinates.lng]}
                                                    zoom={16}
                                                    style={{ height: '100%', width: '100%' }}
                                                    scrollWheelZoom={false}
                                                >
                                                    <TileLayer
                                                        attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
                                                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                    />
                                                    <Marker position={[place.coordinates.lat, place.coordinates.lng]}>
                                                        <Popup>
                                                            {placeName}
                                                        </Popup>
                                                    </Marker>
                                                </MapContainer>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>
                            )}
                        </Container>
                    </Box>
                </Box>
            </Box>
        );
    }
    
    return <NotFound />;
};
 
export default PlaceDetails; 